import React from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useFormikContext } from 'formik'
import { Box, Divider, Grid, IconButton, Skeleton, useMediaQuery, useTheme } from '@mui/material'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'

import {
  MediaInput,
  CodeCard,
  FormTitle,
  FormSubmitButton,
  FormImage,
  HelperWrapper,
  LocaleSelect,
  TextInputWithHelper,
  ScrollToButton,
} from 'src/components'
import { MobileNewButtons } from './mobile-new-buttons.component'
import { MobileEditButtons } from './mobile-edit-buttons.component'
import { ProductNFTMetadataSection } from './nft-metadata'
import {
  CodeInput,
  JourneyAssociationInput,
  ImpactsAssociationInput,
  DescriptionSimpleInput,
  DescriptionRichInput,
  ProductScanExplorerButton,
} from './fields'
import { useAccount, useAuth, useMenuItems, usePrompt } from 'src/hooks'
import {
  EAccountRoles,
  EAccountSubscriptionPlans,
  ETranslationNs,
  IProductByIdRespDto,
  IProductForm,
} from 'src/types'

import { createProductQRLink, createRelativePath } from 'src/common/utils'
import useStyles from './styles'
import { ColoredChip } from 'src/components/colored-chip'
import { ExternalLink } from 'src/assets/icons/v2/ExternalLink'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/langauges-list/constants'
import { DEFAULT_QR_LINK } from 'src/constants'

interface IProductFormProps {
  isAddingNew?: boolean
  isLoading: boolean
  product?: IProductByIdRespDto
  locale?: string
  handleLocale?: (locale: string) => void
  disabled?: boolean
}

const ProductForm = ({
  isAddingNew,
  isLoading,
  product,
  locale,
  handleLocale,
  disabled,
}: IProductFormProps) => {
  const [t] = useTranslation()
  const classes = useStyles({})
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const duplicate = searchParams.get('duplicate')
  const { values, handleChange, errors, setFieldValue, isSubmitting, dirty } =
    useFormikContext<IProductForm>()
  const theme = useTheme()
  const location = useLocation()
  const { data: account, isLoading: isAccountLoading } = useAccount()
  const { isSuperadmin } = useAuth()
  const isAccountOwner = isSuperadmin || account?.currentRoles.includes(EAccountRoles.owner)

  usePrompt({
    message: isLoading ? t('prompt-loading-changes.v') : t('prompt-unsaved-changes.v'),
    when: (dirty && !isSubmitting) || isLoading,
    beforeUnload: true,
  })
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const { handleChooseMenuItem, menuItems } = useMenuItems([
    { title: 'products-views-product-column.v', isActive: true },
  ])

  const breadcrumbs = [
    {
      text: t('products-header-title.v', { ns: ETranslationNs.Navbar }),
      path: createRelativePath(location),
    },
    { text: isAddingNew ? 'product-add-header-title.v' : values.name },
  ]
  const freeAccount = account?.subscription_plan === EAccountSubscriptionPlans.free

  React.useEffect(() => {
    if (duplicate) {
      setFieldValue('name', `${values.name} - ${t('copy-duplicate-postfix.v')}`)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlOpen = (link: string) => {
    window.open(link + '?preview=true', '_blank')
  }

  const qrLink = product?.simple_id
    ? createProductQRLink({ productId: product?.simple_id, lng: locale || DEFAULT_LANGUAGE_CODE })
    : DEFAULT_QR_LINK

  return (
    <Form>
      {!isTablet && (
        <FormTitle
          simpleId={product?.simple_id}
          locale={locale}
          isAddMode={isAddingNew}
          showQRCodeButton
          productStatus={product?.public}
          showStatus
          afterTitleNode={
            product && !product?.ai_generated ? (
              <ProductScanExplorerButton
                locale={locale}
                tokenId={product?.token_id}
                isMinting={product?.is_minting}
                arweaveUri={product?.arweave_url}
                isAccountOwner={!!isAccountOwner}
                notShowNftButton
              />
            ) : null
          }
          // afterBreadcrumbsNode={
          //   locale && handleLocale ? (
          //     <LocaleSelect locale={locale} handleLocale={handleLocale} />
          //   ) : null
          // }
          imageComponent={<FormImage alt={values.name} />}
          menuItems={menuItems}
          handleChooseMenuItem={handleChooseMenuItem}
          breadcrumbs={breadcrumbs}
          setFieldValue={setFieldValue}
          title={values.name}
        />
      )}

      <Grid className={classes.form}>
        <Grid className={classes.formWrapper} container>
          {isTablet && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Grid className={classes.mainImageWrapper}>
                <FormImage alt={values.name} />
                {product && !product?.ai_generated ? (
                  <ProductScanExplorerButton
                    locale={locale}
                    tokenId={product?.token_id}
                    isMinting={product?.is_minting}
                    arweaveUri={product?.arweave_url}
                    isAccountOwner={!!isAccountOwner}
                    notShowNftButton
                  />
                ) : null}
                <ScrollToButton
                  icon={
                    <QRCodeSVG
                      style={{
                        width: '80px',
                        height: '80px',
                      }}
                      value={qrLink}
                      level="H"
                      fgColor={theme.palette.custom.black}
                      bgColor={theme.palette.custom.white}
                    />
                  }
                  referenceId="product-qr-code-section"
                />
              </Grid>
              {!isAddingNew && (
                <ColoredChip
                  mobile
                  color={product?.public ? 'green' : '#E57373'}
                  text={
                    product?.public ? t('product-public-lable.v') : t('product-not-public-lable.v')
                  }
                  icon={
                    product?.public ? null : (
                      <IconButton
                        sx={{
                          padding: '2px',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          handlOpen(
                            createProductQRLink({
                              productId: product?.simple_id || '',
                              lng: locale || DEFAULT_LANGUAGE_CODE,
                            }),
                          )
                        }}
                      >
                        <ExternalLink size={24} stroke={'#E57373'} />
                      </IconButton>
                    )
                  }
                />
              )}
            </Box>
          )}
          {locale && handleLocale && (
            <Box
              sx={{
                // pb: isTablet ? '5px' : '20px',
                pt: isTablet ? '10px' : 0,
                px: '5px',
                alignSelf: 'start',
              }}
            >
              <LocaleSelect locale={locale} handleLocale={handleLocale} />
            </Box>
          )}
          <div className={classes.inputsWrapper}>
            <div className={classes.leftHand}>
              <TextInputWithHelper
                disabled={disabled}
                inputName="name"
                handleChange={handleChange}
                value={values.name}
                error={errors.name ? t(errors.name + '.v') : ''}
                inputPlaceholder={t('products-form-name-input-placeholder.v')}
                inputUpperLabel={
                  <HelperWrapper
                    t={'products-form-name-input-label'}
                    ns={ETranslationNs.Common}
                    isInputLable
                    isMandatory
                  />
                }
              />
              <CodeInput isDisabled={disabled} />
              <TextInputWithHelper
                disabled={disabled}
                inputName="gtin"
                handleChange={handleChange}
                value={values.gtin}
                error={errors.gtin ? t(errors.gtin + '.v') : ''}
                inputPlaceholder={t('products-form-gtin-input-placeholder.v', {
                  ns: ETranslationNs.Form,
                })}
                inputUpperLabel={
                  <HelperWrapper
                    t={'products-form-gtin-input-label'}
                    ns={ETranslationNs.Common}
                    isInputLable
                  />
                }
              />
            </div>
            {/* <div className={classes.rightHand}>
              <div style={{ height: '100%' }}>
                {!isTablet && (
                  <CodeCard
                    isDisabled={disabled}
                    outerLng={locale}
                    productId={id!}
                    simpleProductId={product?.simple_id}
                    satellite_map={product?.satellite_map}
                  />
                )}
              </div>
            </div> */}
          </div>
          <Grid className={classes.descriptionWrapper}>
            {isAccountLoading ? (
              <Skeleton variant="rectangular" height={230} width="100%" />
            ) : freeAccount ? (
              <DescriptionSimpleInput isRequired={false} isDisabled={disabled} locale={locale} />
            ) : (
              <DescriptionRichInput isRequired={false} isDisabled={disabled} locale={locale} />
            )}
          </Grid>
          <MediaInput
            disabled={disabled}
            mainImageSwitch
            inputName="media"
            media={values.media}
            setFieldValue={setFieldValue}
            showSwitch
            withDnd
          />
          <ImpactsAssociationInput isDisabled={disabled} />

          <JourneyAssociationInput isDisabled={disabled} />

          <div id="product-qr-code-section" className={classes.inputsWrapper}>
            <div style={{ height: '100%' }}>
              <CodeCard
                isDisabled={disabled}
                outerLng={locale}
                productId={id!}
                simpleProductId={product?.simple_id}
                satellite_map={product?.satellite_map}
              />
            </div>
          </div>

          {product?.token_id && product?.arweave_url && (
            <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', width: '100%' }} />
          )}

          <ProductNFTMetadataSection
            locale={locale}
            product={product}
            account={account || undefined}
            isSuperadmin={isSuperadmin}
            isLoading={isAccountLoading || isLoading}
          />

          {isTablet &&
            (isAddingNew ? <MobileNewButtons isDisabled={disabled} /> : <MobileEditButtons />)}
        </Grid>
        {!isTablet && isAddingNew && (
          <FormSubmitButton
            notDisabledIfDirty={!!duplicate}
            withoutIcon
            validText={<HelperWrapper t="save-button-text" isColorInherited />}
            invalidText={<HelperWrapper t="save-button-text" isColorInherited />}
          />
        )}
        {!isAddingNew && (
          <FormSubmitButton
            validText={
              <HelperWrapper
                t="save-changes-button-text"
                ns={ETranslationNs.Form}
                isColorInherited
              />
            }
            invalidText={<HelperWrapper t="products-form-submit-button" isColorInherited />}
          />
        )}
      </Grid>
    </Form>
  )
}

export { ProductForm }
